/**
 * Auth state — wraps signet-login.
 *
 * Three login paths offered, all funnelled through Signet.login(). The game
 * requires a live signer for scores, claims, replays, uploads, and zaps; an
 * identity-only auth response is rejected at the auth boundary.
 *
 * Both calls are wrapped with timeouts because window.nostr / NIP-46 bunker
 * relays have no native cancellation — if the signer is cold or unreachable
 * the underlying promise can hang for tens of seconds without resolving. The
 * Signet modal itself owns its UI so we can't dismiss it from here, but we
 * can at least free our own state and surface the error to the player.
 */

import type { ConsumeCallbackResult, SignetSession, SignetAuthEvent } from 'signet-login';
import { getActiveRelays } from './relays.js';
import { serialiseSigner } from './sign-queue.js';
import { getGuestRecord, loadOrCreateGuest } from './guest.js';
import { FaucetSigner, fetchKioskInfo } from './faucet-signer.js';
import { BUILD_ID } from './version.js';
import { consumeArcadeHandoff } from './arcade-handoff.js';

let lastSignerInfoLogKey = '';

const SIGNET_AUTH_RELAY_PRIORITY = [
  'wss://relay.trotters.cc',
  'wss://nos.lol',
  'wss://relay.primal.net',
  'wss://relay.ditto.pub',
  'wss://relay.damus.io',
] as const;

function pickSignetAuthRelay(): string | undefined {
  const active = getActiveRelays();
  return SIGNET_AUTH_RELAY_PRIORITY.find(relay => active.includes(relay)) ?? active[0];
}

export class AuthOnlySignerError extends Error {
  constructor() {
    super('My Signet approved identity only, but Pallasite needs a live signer. Turn on Bunker in My Signet for this identity, paste a bunker URI, use a browser extension, or play as guest.');
    this.name = 'AuthOnlySignerError';
  }
}

export function isAuthOnlySignerError(err: unknown): err is AuthOnlySignerError {
  return err instanceof AuthOnlySignerError
    || (err instanceof Error && err.name === 'AuthOnlySignerError');
}

/**
 * Wrap a freshly-resolved session's signer so every signEvent goes through
 * the global serialised queue. Centralised here so every login path —
 * signIn, tryRestore, handleAuthCallback — returns a wrapped signer and
 * downstream call sites in main.ts / ui.ts don't need to remember to wrap.
 * Without this, heartbeat + replay chunk publishes + claim signs race and
 * jam the underlying NIP-46 / NIP-07 extension.
 */
function wrapSession(session: SignetSession | null): SignetSession | null {
  if (!session) return null;
  // Announce the resolved signer capability the moment a session is set, so a
  // silent auth-only fallback (login OK but can't sign — heartbeats/scores/
  // claims get skipped) is visible instead of being discovered mid-run.
  // Inspect live via window.__signetSignerInfo.
  try {
    const canSign = session.signer?.capabilities?.canSignEvents === true;
    const info = { method: session.method, canSignEvents: canSign, pubkey: session.pubkey };
    (globalThis as { __signetSignerInfo?: typeof info }).__signetSignerInfo = info;
    const logKey = `${session.method}:${canSign ? 'sign' : 'auth'}:${session.pubkey}`;
    if (logKey !== lastSignerInfoLogKey) {
      lastSignerInfoLogKey = logKey;
      console.warn(
        `[auth] session ready — method=${session.method} canSignEvents=${canSign} pubkey=${session.pubkey?.slice(0, 12)}…`
        + (canSign ? '' : '  ⚠ AUTH-ONLY: this session CANNOT sign. The signer device handed back no live bunker (no bunker:// URI, or the reconnect to it failed). Signing-dependent features will be skipped until you get a live signer.'),
      );
    }
  } catch { /* diagnostics must never break login */ }
  return { ...session, signer: serialiseSigner(session.signer) };
}

/**
 * True when the session's signer can actually produce signatures (NIP-07
 * extension, live bunker, nsec, guest, kiosk) — false for an auth-only
 * identity-proof session, e.g. a cross-device QR sign-in that handed back no
 * live bunker.
 *
 * Pallasite ACCEPTS auth-only sessions: sign-in succeeds, identity is proven,
 * and the leaderboard is game-signed (the player is a `p` tag — see score.ts).
 * Feature code that genuinely needs the player's own signature gates on this
 * and degrades — skip a fire-and-forget publish, or call assertCanSign() for an
 * explicit user action so the UI prompts for a live signer — instead of calling
 * signEvent on a signer that can't. (Previously the auth boundary threw
 * AuthOnlySignerError here and logged the session out, which made cross-device
 * QR sign-in impossible whenever the signer came back auth-only.)
 */
export function sessionCanSign(session: SignetSession | null | undefined): boolean {
  return session?.signer?.capabilities?.canSignEvents === true;
}

/**
 * Throw AuthOnlySignerError unless the session can sign. For explicit,
 * user-initiated signer actions (sats claim/withdraw, zap) where the UI already
 * catches AuthOnlySignerError and prompts for a live signer / extension.
 */
export function assertCanSign(session: SignetSession | null | undefined): void {
  if (!sessionCanSign(session)) throw new AuthOnlySignerError();
}

/**
 * Result shape from signet-verify's verifyAge() — vendored to keep the
 * typecheck self-contained. Source of truth: `signet-verify/src/signet-verify.ts`
 * (`SignetVerifyResult`). Drift is detectable: we only consume the fields
 * below, so any rename will surface as a type error here.
 */
export interface SignetVerifyResult {
  verified: boolean;
  ageRange: string | null;
  tier: number | null;
  verifierPubkey: string | null;
  verifierConfirmed: boolean | null;
  expiresAt: number | null;
  error?: string;
}

export interface SignetVerifyOptions {
  relayUrl?: string;
  theme?: 'light' | 'dark' | 'auto';
  timeout?: number;
  verifierCheckUrl?: string | null;
  acceptUnconfirmed?: boolean;
}

export type SignInMethod = 'nip07' | 'redirect' | 'qr' | 'bunker' | 'nsec' | 'amber';

declare global {
  interface Window {
    Signet?: {
      login: (opts: {
        appName: string;
        theme?: 'light' | 'dark' | 'auto';
        relayUrl?: string;
        signetAppOrigin?: string;
        mode?: 'relay' | 'redirect';
        redirectCallback?: string;
        preferredMethod?: SignInMethod;
      }) => Promise<SignetSession | null>;
      restoreSession: () => Promise<SignetSession | null>;
      logout: (s?: SignetSession) => Promise<void>;
      handleRedirectCallback: (options?: { waitForBunker?: boolean }) => Promise<ConsumeCallbackResult>;
      verifyAge?: (
        requiredAgeRange: string,
        options?: SignetVerifyOptions,
      ) => Promise<SignetVerifyResult>;
    };
  }
}

export const APP_NAME = 'Pallasite';
export const GAME_ID = 'pallasite';

export async function consumeArcadeSession(): Promise<SignetSession | null> {
  return wrapSession(await consumeArcadeHandoff(GAME_ID));
}

/** Cap interactive sign-in at 3 min. The QR / cross-device flow (scan → approve →
 *  a hardware signer such as an ESP32 doing two physical signings) routinely runs
 *  to ~30s+ — the old 20s fired "Signer didn't respond" mid-sign even though
 *  signet-login's own 120s relay-wait and the response were perfectly fine. Keep
 *  this above the SDK's internal timeout so the SDK's real outcome wins. */
const SIGN_IN_TIMEOUT_MS = 180_000;
/** Session restoration on boot is best-effort; don't block the title screen. */
const RESTORE_TIMEOUT_MS = 5_000;
const SDK_CACHE_BUST = encodeURIComponent(BUILD_ID);
const SIGNET_VERIFY_SRC = `/signet-verify.iife.js?v=${SDK_CACHE_BUST}`;
const SIGNET_LOGIN_SRC = `/signet-login.iife.js?v=${SDK_CACHE_BUST}`;
const SIGNET_APP_ORIGIN_STORAGE_KEY = 'pallasite:signet.appOrigin';
const SIGNET_STORAGE_KEYS = {
  pubkey: 'signet:login.pubkey',
  method: 'signet:login.method',
  authEvent: 'signet:login.authEvent',
} as const;
const SIGNET_CALLBACK_PARAMS = ['error', 'pubkey', 'signature', 'eventId'] as const;

let signetLoadPromise: Promise<boolean> | null = null;

export class SignInTimeoutError extends Error {
  constructor() {
    super("Signer didn't respond. Make sure your Nostr extension is unlocked, then try again.");
    this.name = 'SignInTimeoutError';
  }
}

function withTimeout<T>(p: Promise<T>, ms: number, onTimeout: () => Error): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(onTimeout()), ms);
    p.then(
      v => { window.clearTimeout(timer); resolve(v); },
      e => { window.clearTimeout(timer); reject(e); },
    );
  });
}

function signetLoginReady(): boolean {
  return typeof window !== 'undefined'
    && typeof window.Signet?.login === 'function'
    && typeof window.Signet.restoreSession === 'function'
    && typeof window.Signet.handleRedirectCallback === 'function';
}

function loadScript(src: string, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(id) as HTMLScriptElement | null;
    if (existing?.dataset.loaded === '1') {
      resolve();
      return;
    }
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error(`failed to load ${src}`)), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.onload = () => {
      script.dataset.loaded = '1';
      resolve();
    };
    script.onerror = () => reject(new Error(`failed to load ${src}`));
    document.head.appendChild(script);
  });
}

async function ensureSignetLoaded(): Promise<boolean> {
  if (signetLoginReady()) return true;
  if (typeof document === 'undefined') return false;
  if (!signetLoadPromise) {
    signetLoadPromise = (async () => {
      // signet-verify assigns window.Signet; signet-login merges into it.
      // Loading in the reverse order drops login methods.
      await loadScript(SIGNET_VERIFY_SRC, 'pallasite-signet-verify-sdk');
      await loadScript(SIGNET_LOGIN_SRC, 'pallasite-signet-login-sdk');
      return signetLoginReady();
    })().catch((err) => {
      console.warn('[auth] failed to load Signet SDK:', err);
      signetLoadPromise = null;
      return false;
    });
  }
  return signetLoadPromise;
}

export function hasSignetRedirectCallback(): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    return SIGNET_CALLBACK_PARAMS.some((key) => params.has(key));
  } catch {
    return false;
  }
}

function hasStoredSignetSession(): boolean {
  try {
    return !!(
      localStorage.getItem(SIGNET_STORAGE_KEYS.pubkey)
      && localStorage.getItem(SIGNET_STORAGE_KEYS.method)
      && localStorage.getItem(SIGNET_STORAGE_KEYS.authEvent)
    );
  } catch {
    return false;
  }
}

function normaliseSignetAppOrigin(raw: string | null): string | undefined {
  if (!raw) return undefined;
  try {
    const url = new URL(raw.trim());
    const host = url.hostname.toLowerCase();
    const isLocalHost = host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local');
    const isMySignetHost = host === 'mysignet.app' || host === 'www.mysignet.app';
    if (url.protocol !== 'https:' && !(url.protocol === 'http:' && (isLocalHost || isMySignetHost))) {
      return undefined;
    }
    url.pathname = '';
    url.search = '';
    url.hash = '';
    return url.toString().replace(/\/$/, '');
  } catch {
    return undefined;
  }
}

function configuredSignetAppOrigin(): string | undefined {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = normaliseSignetAppOrigin(
      params.get('mysignet') ?? params.get('signetAppOrigin') ?? params.get('signet_app_origin'),
    );
    if (fromUrl) {
      localStorage.setItem(SIGNET_APP_ORIGIN_STORAGE_KEY, fromUrl);
      return fromUrl;
    }
    return normaliseSignetAppOrigin(localStorage.getItem(SIGNET_APP_ORIGIN_STORAGE_KEY));
  } catch {
    return undefined;
  }
}

export async function signIn(): Promise<SignetSession | null> {
  if (!(await ensureSignetLoaded()) || !window.Signet) return null;
  // Use the player's chosen relay for the cross-device QR path — the SDK's
  // wss://relay.damus.io default doesn't match what publishing/scoring use,
  // so a relay-mode handshake there would be cross-traffic the game never
  // sees. Fall back to the SDK default only if zero relays are enabled.
  const relayUrl = pickSignetAuthRelay();
  const signetAppOrigin = configuredSignetAppOrigin();
  const session = await withTimeout(
    window.Signet.login({
      appName: APP_NAME,
      theme: 'dark',
      ...(relayUrl ? { relayUrl } : {}),
      ...(signetAppOrigin ? { signetAppOrigin } : {}),
    }),
    SIGN_IN_TIMEOUT_MS,
    () => new SignInTimeoutError(),
  );
  return wrapSession(session);
}

/**
 * Open the SDK login flow but skip its picker by forcing a specific
 * method. Used by the controller PWA to surface method-first buttons
 * (bunker / nsec / extension / Signet) without leading with Signet
 * branding — privacy-conscious nostr users see
 * "paste bunker URI" as the headline, not "Sign in with Signet".
 */
export async function signInWith(method: SignInMethod): Promise<SignetSession | null> {
  if (!(await ensureSignetLoaded()) || !window.Signet) return null;
  const relayUrl = pickSignetAuthRelay();
  const signetAppOrigin = configuredSignetAppOrigin();
  const session = await withTimeout(
    window.Signet.login({
      appName: APP_NAME,
      theme: 'dark',
      preferredMethod: method,
      ...(relayUrl ? { relayUrl } : {}),
      ...(signetAppOrigin ? { signetAppOrigin } : {}),
    }),
    SIGN_IN_TIMEOUT_MS,
    () => new SignInTimeoutError(),
  );
  return wrapSession(session);
}

/**
 * Consume an in-flight redirect callback if the URL has one. Call once on
 * boot, before tryRestore — a fresh redirect-mode login persists a session
 * via the standard storage layer, so the next tryRestore picks it up. The
 * tagged-union return is ignored here; consumers that want to surface
 * 'denied' / 'invalid' to the UI can call window.Signet.handleRedirectCallback
 * directly.
 */
export async function handleAuthCallback(): Promise<SignetSession | null> {
  if (!hasSignetRedirectCallback() && !window.Signet?.handleRedirectCallback) return null;
  if (!(await ensureSignetLoaded()) || !window.Signet?.handleRedirectCallback) return null;
  try {
    const result = await window.Signet.handleRedirectCallback({ waitForBunker: true });
    // Whether the callback resolved to a session, denied, or invalid, the
    // SDK should have torn down its dialog. Reports of "buttons don't work
    // after returning from sign-in" are consistent with a stale dialog
    // covering the title screen — belt-and-braces sweep here keeps the
    // title interactive even if the SDK leaves something behind.
    if (result.kind !== 'no-callback') sweepSignetArtefacts();
    return result.kind === 'session' ? wrapSession(result.session) : null;
  } catch (err) {
    if (isAuthOnlySignerError(err)) {
      sweepSignetArtefacts();
      throw err;
    }
    // The SDK already logs invalid-callback diagnostics. Sweep artefacts
    // and swallow so a stray bookmark with stale params can't strand the UI.
    console.warn('[auth] handleRedirectCallback threw:', err);
    sweepSignetArtefacts();
    return null;
  }
}

/**
 * Remove any Signet SDK dialog/callback overlays from the DOM. Called after
 * handleRedirectCallback resolves so a stale modal can't intercept clicks
 * on the title screen. Also strips the URL hash if one survived consume.
 *
 * Exported so main.ts can call it on bfcache restore: a player who hits
 * browser-back mid-redirect comes back to a frozen page that still has the
 * SDK dialog in the DOM, and the dialog captures every click underneath.
 *
 * The SDK uses native <dialog> elements with IDs (not classes); a dialog left
 * open via showModal() sits in the top layer and is opaque to clicks even
 * when not visually evident. Closing first is belt-and-braces for browsers
 * that need explicit close before remove to release the top layer.
 */
export function sweepSignetArtefacts(): void {
  for (const id of ['signet-login-dialog', 'signet-verify-dialog']) {
    const el = document.getElementById(id);
    if (!el) continue;
    if (el instanceof HTMLDialogElement && el.open) {
      try { el.close(); } catch { /* ignore */ }
    }
    el.remove();
  }
  if (location.hash) {
    history.replaceState(null, '', location.pathname + location.search);
  }
}

export async function tryRestore(): Promise<SignetSession | null> {
  // Signet-restored session wins if one exists — a returning user who
  // previously upgraded their guest identity to a real NIP-07 / bunker
  // account should land back on that real identity, not regress to the
  // shadow guest record we may have kept around from before the
  // upgrade.
  if (window.Signet || hasStoredSignetSession()) {
    try {
      if (!(await ensureSignetLoaded()) || !window.Signet) throw new Error('signet-load-failed');
      const session = await withTimeout(
        window.Signet.restoreSession(),
        RESTORE_TIMEOUT_MS,
        () => new Error('restore-timeout'),
      );
      if (session) {
        // Accept auth-only restores too (see sessionCanSign). The previous
        // logout-and-drop of non-nip07 auth-only sessions regressed a returning
        // player to guest whenever their stored Signet session couldn't sign.
        return wrapSession(session);
      }
    } catch {
      // Restore is best-effort on boot — silently fall through to the
      // guest path if the signer doesn't respond in time.
    }
  }
  // No Signet session — fall back to the seamless guest identity if
  // we've created one previously. First-time visitors get null and the
  // title screen prompts them for a name before creating their guest
  // keypair.
  const guest = getGuestRecord();
  if (!guest) return null;
  try {
    const session = await loadOrCreateGuest({ name: guest.name });
    return wrapSession(session);
  } catch {
    return null;
  }
}

export async function signOut(currentSession: SignetSession | null): Promise<void> {
  if ((currentSession?.method as string | undefined) === 'guest') return;
  if (!(await ensureSignetLoaded()) || !window.Signet) return;
  await window.Signet.logout(currentSession ?? undefined);
}

/**
 * Create or restore a seamless guest identity and return it as a
 * SignetSession with the standard sign-queue wrap applied. The title
 * screen uses this to provision a local keypair the first time a
 * visitor types their name and ignites — subsequent visits land in
 * tryRestore which reads the same localStorage record.
 *
 * `followPallasite` controls whether the fire-and-forget kind 3
 * contact-list publish includes the Pallasite game npub. Defaults to
 * true (pre-checked opt-out checkbox on the auth screen); pass false
 * for a guest who explicitly unticked it.
 */
export async function createGuestSession(
  name: string,
  opts: { followPallasite?: boolean; fresh?: boolean } = {},
): Promise<SignetSession> {
  const session = await loadOrCreateGuest({
    name,
    followPallasite: opts.followPallasite ?? true,
    fresh: opts.fresh ?? false,
  });
  const wrapped = wrapSession(session);
  if (!wrapped) throw new Error('guest-session-wrap-failed');
  return wrapped;
}

/**
 * Establish an unattended-booth session that signs via the self-hosted
 * faucet's KIOSK_NSEC (server-side), so the key never reaches the browser.
 *
 * For a self-hosted deploy that must sign (replays, NIP-98 uploads) without an
 * interactive "Sign in with Signet" — which on these flows lands auth-only.
 * Probes /api/kiosk/info; if no kiosk identity is configured (normal/production
 * deploys), returns null so the caller falls through to the normal picker.
 */
export async function createKioskSession(): Promise<SignetSession | null> {
  const info = await fetchKioskInfo();
  if (!info || !info.pubkey) return null;
  const signer = new FaucetSigner(info.pubkey);
  // Synthetic kind-21236 auth event, signed by the kiosk key via the faucet.
  // Mirrors the guest path: downstream reads tags but doesn't re-verify.
  const authEvent = (await signer.signEvent({
    kind: 21236,
    content: '',
    tags: [
      ['app', 'pallasite'],
      ['method', 'kiosk'],
      ['challenge', crypto.randomUUID()],
    ],
    created_at: Math.floor(Date.now() / 1000),
  })) as SignetAuthEvent;
  const session: SignetSession = {
    pubkey: info.pubkey,
    method: 'bunker',
    signer,
    authEvent,
    displayName: 'Pallasite Kiosk',
  };
  const wrapped = wrapSession(session);
  if (!wrapped) throw new Error('kiosk-session-wrap-failed');
  return wrapped;
}
